import {BaseApi} from './BaseApi'
import {sanitizePayload} from '../utils/apiUtils'
import {WCCodeImportCatalogResult, WCCodeImportResult} from './wcCodeApi'
import {reportCatchError} from '../utils/errorReporter'

// WC Catalog History Entry Interface
export interface WCCatalogHistoryEntry {
  action: 'ADDED' | 'REMOVED' | 'UPDATED'
  wcCodeIds: string[]
  userName: string
  userRole: string
  editDate: string
}

// WC Catalog Interfaces
export interface WCCatalog {
  id: string
  catalogName: string
  carrierName: string
  policyNumber: string
  effectiveDate: string
  expirationDate: string
  state: string
  status: 'Active' | 'Expired' | 'Draft'
  wcCodeIds: string[] // Array of WC Code IDs associated with this catalog
  createdDate?: Date
  modifiedDate?: Date
  createdBy?: string
  modifiedBy?: string
  versionKey?: number
}

export interface CreateWCCatalogRequest {
  catalogName: string
  insuranceCarrier: string
  policyNumber: string
  effectiveDate: string
  expirationDate: string
  state: string
  wcCodeIds?: string[]
}

export interface UpdateWCCatalogRequest {
  id: string
  catalogName: string
  insuranceCarrier: string
  policyNumber: string
  effectiveDate: string
  expirationDate: string
  state: string
  wcCodeIds?: string[]
  versionKey?: number
}

export interface WCCatalogResponse {
  id: string
  catalogName: string
  insuranceCarrier: string // API uses 'insuranceCarrier' not 'carrierName'
  carrierName?: string // Keep for backward compatibility
  policyNumber: string
  effectiveDate: string
  expirationDate: string
  state?: string // Some catalogs don't have state
  status: 'Active' | 'Inactive' | 'Expired' | 'Draft'
  isActive: boolean // Additional field from API
  wcCodeIds: string[]
  wcCodeCount: number // Count of associated WC codes
  history?: WCCatalogHistoryEntry[] // History of changes
  createdDate: Date
  modifiedDate: Date
  createdBy: string
  modifiedBy: string
  versionKey: number
}

export interface WCCatalogPagingModel {
  data: WCCatalogResponse[]
  totalCount: number
  currentPage: number
  pageSize: number
  totalPages: number
}

export interface WCCatalogSearchRequest {
  catalogName?: string
  insuranceCarrier?: string
  policyNumber?: string
  effectiveDate?: string
  expirationDate?: string
  status?: 'Incomplete' | 'Active' | 'InActive' | string[]
  pageNumber?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

export interface RenewWCCatalogRequest {
  catalogId: string
  expirationDate: string
  versionKey?: number
}

export interface UpdateWcCodesInCatalogRequest {
  catalogId: string
  wccodes: Array<{
    wcCodeId: string
    rateType: string
    rate: number
    multiplier: number
    effectiveRate: number
    _id: string
  }>
}

// Paging Parameters Interface
export interface PagingParametersDto {
  pageNumber?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

/**
 * WC Catalog API Service
 * Handles all workers' compensation catalog related operations
 */
export class WCCatalogApi extends BaseApi {
  /**
   * Get paginated list of WC Catalogs
   */
  async getWCCatalogs(pagingParams?: PagingParametersDto): Promise<WCCatalogPagingModel> {
    try {
      const params = new URLSearchParams()
      if (pagingParams?.pageNumber) params.append('pageNumber', pagingParams.pageNumber.toString())
      if (pagingParams?.pageSize) params.append('pageSize', pagingParams.pageSize.toString())
      if (pagingParams?.sortBy) params.append('sortBy', pagingParams.sortBy)
      if (pagingParams?.sortDirection) params.append('sortDirection', pagingParams.sortDirection)

      const response = await this.client.get<WCCatalogPagingModel>(
        `/wc-catalog/getWCCatalogs?${params.toString()}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC catalogs:', error)
      throw error
    }
  }

  /**
   * Get all WC Catalogs (non-paginated)
   */
  async getAllWCCatalogs(): Promise<WCCatalogResponse[]> {
    try {
      const response = await this.client.get<WCCatalogResponse[]>('/wc-catalog/getAllWCCatalogs')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching all WC catalogs:', error)
      throw error
    }
  }

  /**
   * Get WC Catalog by ID
   */
  async getWCCatalogById(id: string): Promise<WCCatalogResponse> {
    try {
      const response = await this.client.get<WCCatalogResponse>(`/wc-catalog/getWCCatalog/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC catalog by ID:', error)
      throw error
    }
  }

  /**
   * Search WC Catalogs with multiple criteria
   */
  async searchWCCatalogs(searchRequest: WCCatalogSearchRequest): Promise<WCCatalogResponse[]> {
    try {
      const params = new URLSearchParams()

      Object.entries(searchRequest).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          // Handle status array specially - append multiple status parameters
          if (key === 'status' && Array.isArray(value)) {
            value.forEach((status) => params.append('status', status))
          } else {
            params.append(key, value.toString())
          }
        }
      })

      const response = await this.client.get<WCCatalogResponse[]>(
        `/wc-catalog/searchWCCatalogs?${params.toString()}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error searching WC catalogs:', error)
      throw error
    }
  }

  /**
   * Get WC Catalogs by state
   */
  async getWCCatalogsByState(state: string): Promise<WCCatalogResponse[]> {
    try {
      const response = await this.client.get<WCCatalogResponse[]>(
        `/wc-catalog/getWCCatalogsByState/${state}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC catalogs by state:', error)
      throw error
    }
  }

  /**
   * Create a new WC Catalog (Platform Admin only)
   */
  async createWCCatalog(catalog: CreateWCCatalogRequest): Promise<WCCatalogResponse> {
    try {
      const sanitizedData = sanitizePayload(catalog)
      const response = await this.client.post<WCCatalogResponse>(
        '/wc-catalog/createWCCatalog',
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating WC catalog:', error)
      throw error
    }
  }

  /**
   * Update an existing WC Catalog (Platform Admin only)
   */
  async updateWCCatalog(catalog: UpdateWCCatalogRequest): Promise<WCCatalogResponse> {
    try {
      const sanitizedData = sanitizePayload(catalog)
      const response = await this.client.put<WCCatalogResponse>(
        '/wc-catalog/updateWCCatalog',
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating WC catalog:', error)
      throw error
    }
  }

  /**
   * Delete a WC Catalog (Platform Admin only)
   */
  async deleteWCCatalog(id: string): Promise<WCCatalogResponse> {
    try {
      const response = await this.client.delete<WCCatalogResponse>(
        `/wc-catalog/deleteWCCatalog/${id}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error deleting WC catalog:', error)
      throw error
    }
  }

  /**
   * Add WC Codes to Catalog with Rate Information (Updated Backend Schema)
   *
   * New payload structure supports multiple WC codes with rate information.
   * Each code includes wcCodeId, rateType, rate, multiplier, and effectiveRate.
   */
  async addWCCodesToCatalog(data: {
    catalogId: string
    wccodes: Array<{
      wcCodeId: string
      rateType: 'Manual' | string
      rate: number
      multiplier: number
      effectiveRate: number
    }>
  }): Promise<WCCatalogResponse> {
    try {
      const sanitizedData = sanitizePayload(data)
      const response = await this.client.post<WCCatalogResponse>(
        '/wc-catalog/addWCCodesToCatalog',
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error adding WC codes to catalog:', error)
      throw error
    }
  }

  /**
   * Add Single WC Code to Catalog (Helper method for backward compatibility)
   */
  async addWCCodeToCatalog(data: {
    catalogId: string
    wcCodeId: string
    rateType: 'Manual' | string
    rate: number
    multiplier: number
    effectiveRate: number
  }): Promise<WCCatalogResponse> {
    return this.addWCCodesToCatalog({
      catalogId: data.catalogId,
      wccodes: [
        {
          wcCodeId: data.wcCodeId,
          rateType: data.rateType,
          rate: data.rate,
          multiplier: data.multiplier,
          effectiveRate: data.effectiveRate,
        },
      ],
    })
  }

  /**
   * Remove WC Codes from a catalog
   */
  async removeWCCodesFromCatalog(
    catalogId: string,
    wcCodeIds: string[],
  ): Promise<WCCatalogResponse> {
    try {
      const response = await this.client.post<WCCatalogResponse>(
        `/wc-catalog/removeWCCodesFromCatalog/${catalogId}`,
        {wcCodeIds},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error removing WC codes from catalog:', error)
      throw error
    }
  }

  /**
   * Get WC Codes associated with a catalog
   */
  async getWCCodesByCatalogId(catalogId: string): Promise<any[]> {
    try {
      const response = await this.client.get<any[]>(`/wc-catalog/getWCCodesByCatalog/${catalogId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC codes by catalog ID:', error)
      throw error
    }
  }

  /**
   * Export WC Catalog data
   */
  async exportWCCatalog(catalogId: string, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    try {
      const response = await this.client.get(`/wc-catalog/exportWCCatalog/${catalogId}`, {
        params: {format},
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error exporting WC catalog:', error)
      throw error
    }
  }

  /**
   * Import WC Catalogs from file (Platform Admin only)
   */
  async importWCCatalogs(file: File, validateOnly: boolean = false): Promise<any> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('validateOnly', validateOnly.toString())

      const response = await this.client.post('/wc-catalog/importWCCatalogs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error importing WC catalogs:', error)
      throw error
    }
  }

  /**
   * Get WC Catalogs by Agency ID
   */
  async getWCCatalogsByAgencyId(agencyId: string): Promise<WCCatalogResponse[]> {
    try {
      const response = await this.client.get<WCCatalogResponse[]>(
        `/wc-catalog/getWCCatalogsByAgency/${agencyId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC catalogs by agency ID:', error)
      throw error
    }
  }

  /**
   * Assign WC Catalog to Agency
   */
  async assignCatalogToAgency(catalogId: string, agencyId: string): Promise<WCCatalogResponse> {
    try {
      const response = await this.client.post<WCCatalogResponse>(
        `/wc-catalog/assignCatalogToAgency/${catalogId}`,
        {agencyId},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error assigning catalog to agency:', error)
      throw error
    }
  }

  /**
   * Remove WC Catalog from Agency
   */
  async removeCatalogFromAgency(catalogId: string, agencyId: string): Promise<WCCatalogResponse> {
    try {
      const response = await this.client.post<WCCatalogResponse>(
        `/wc-catalog/removeCatalogFromAgency/${catalogId}`,
        {agencyId},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error removing catalog from agency:', error)
      throw error
    }
  }

  /**
   * Update WC Catalog Status
   */
  async updateCatalogStatus(
    catalogId: string,
    status: 'Active' | 'Expired' | 'Draft',
  ): Promise<WCCatalogResponse> {
    try {
      const response = await this.client.patch<WCCatalogResponse>(
        `/wc-catalog/updateCatalogStatus/${catalogId}`,
        {status},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating catalog status:', error)
      throw error
    }
  }

  /**
   * Clone an existing WC Catalog
   */
  async cloneCatalog(catalogId: string, newCatalogName: string): Promise<WCCatalogResponse> {
    try {
      const response = await this.client.post<WCCatalogResponse>(
        `/wc-catalog/cloneCatalog/${catalogId}`,
        {catalogName: newCatalogName},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error cloning catalog:', error)
      throw error
    }
  }

  /**
   * Get WC Catalog Templates
   */
  async getCatalogTemplates(): Promise<WCCatalogResponse[]> {
    try {
      const response = await this.client.get<WCCatalogResponse[]>('/wc-catalog/getCatalogTemplates')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching catalog templates:', error)
      throw error
    }
  }

  /**
   * Create WC Catalog from Template
   */
  async createCatalogFromTemplate(
    templateId: string,
    catalogData: CreateWCCatalogRequest,
  ): Promise<WCCatalogResponse> {
    try {
      const sanitizedData = sanitizePayload(catalogData)
      const response = await this.client.post<WCCatalogResponse>(
        `/wc-catalog/createFromTemplate/${templateId}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating catalog from template:', error)
      throw error
    }
  }

  /**
   * Get WC Catalog Change History
   */
  async getCatalogHistory(catalogId: string): Promise<any[]> {
    try {
      const response = await this.client.get<any[]>(`/wc-catalog/getCatalogHistory/${catalogId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching catalog history:', error)
      throw error
    }
  }

  /**
   * Get Agencies that have access to a specific catalog
   */
  async getCatalogSharedAgencies(catalogId: string): Promise<{id: string; name: string}[]> {
    try {
      const response = await this.client.get<{id: string; name: string}[]>(
        `/wc-catalog/getSharedAgencies/${catalogId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching catalog shared agencies:', error)
      throw error
    }
  }

  /**
   * Bulk Update WC Catalogs
   */
  async bulkUpdateCatalogs(
    updates: {catalogId: string; updates: Partial<UpdateWCCatalogRequest>}[],
  ): Promise<WCCatalogResponse[]> {
    try {
      const response = await this.client.post<WCCatalogResponse[]>(
        '/wc-catalog/bulkUpdateCatalogs',
        {updates},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error bulk updating catalogs:', error)
      throw error
    }
  }

  /**
   * Bulk Delete WC Catalogs
   */
  async bulkDeleteCatalogs(catalogIds: string[]): Promise<{deletedCount: number; errors: any[]}> {
    try {
      const response = await this.client.post<{deletedCount: number; errors: any[]}>(
        '/wc-catalog/bulkDeleteCatalogs',
        {catalogIds},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error bulk deleting catalogs:', error)
      throw error
    }
  }

  /**
   * Renew WC Catalog with new expiration date
   */
  async renewWCCatalog(renewData: RenewWCCatalogRequest): Promise<WCCatalogResponse> {
    try {
      const sanitizedData = sanitizePayload(renewData)
      const response = await this.client.put<WCCatalogResponse>(
        '/wc-catalog/renewWCCatalog',
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error renewing WC catalog:', error)
      throw error
    }
  }

  /**
   * Update WC Codes in Catalog
   * PUT /wc-catalog/updateWcCodesInCatalog
   */
  async updateWcCodesInCatalog(
    updateData: UpdateWcCodesInCatalogRequest,
  ): Promise<WCCatalogResponse> {
    try {
      const sanitizedData = sanitizePayload(updateData)
      const response = await this.client.put<WCCatalogResponse>(
        '/wc-catalog/updateWcCodesInCatalog',
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating WC codes in catalog:', error)
      throw error
    }
  }

  /**
   * Import WC Codes from CSV and assign them to a catalog with rates (Platform Admin only)
   * POST /wc-catalog/importWCCodesWithCatalog
   */
  async importWCCodesWithCatalog(catalogId: string, file: File): Promise<WCCodeImportResult> {
    try {
      const formData = new FormData()
      formData.append('catalogId', catalogId)
      formData.append('file', file)

      const response = await this.client.post<WCCodeImportCatalogResult>(
        '/wc-catalog/importWCCodesWithCatalog',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error importing WC codes to catalog:', error)
      throw error
    }
  }
}

// Export singleton instance
export const wcCatalogApi = new WCCatalogApi()
